import { Box, Button, CheckIcon } from '@iheartjane/reefer';
import type { ReactNode } from 'react';

import styles from './addToBagButton.module.css';

function AddToBagLabel({
  atMaxQuantity,
  cartCount,
  hasMultipleWeights,
  isAddingToCart,
}: {
  atMaxQuantity: boolean;
  cartCount: number;
  hasMultipleWeights: boolean;
  isAddingToCart: boolean;
}) {
  if (isAddingToCart) return null; // the button shows loading state

  return (
    <>
      {atMaxQuantity && 'Limit reached'}
      {!atMaxQuantity && (hasMultipleWeights ? 'Select weight' : 'Add to bag')}
      {cartCount ? (
        <Box className={styles['count']} height={24} ml={8} width={24}>
          {cartCount}
        </Box>
      ) : null}
    </>
  );
}

export interface AddToBagButtonProps {
  atMaxQuantity?: boolean;
  cartCount?: number;
  hasMultipleWeights?: boolean;
  isAddingToCart?: boolean;
  /**
   * Single-weight add-to-bag handler. Used by the default button; ignored when
   * {@link renderAddToBag} is provided.
   */
  onAddToBag?: () => void;
  onSelectWeight?: () => void;
  /**
   * Override the single-weight add control. SSR can provide `<Form>`
   * for no-JS add-to-cart.
   */
  renderAddToBag?: (args: { disabled: boolean; label: ReactNode }) => ReactNode;
}

export function AddToBagButton({
  atMaxQuantity = false,
  cartCount = 0,
  hasMultipleWeights = false,
  isAddingToCart = false,
  onAddToBag,
  onSelectWeight,
  renderAddToBag,
}: AddToBagButtonProps) {
  const disabled = isAddingToCart || atMaxQuantity;
  const label = (
    <AddToBagLabel
      atMaxQuantity={atMaxQuantity}
      cartCount={cartCount}
      hasMultipleWeights={hasMultipleWeights}
      isAddingToCart={isAddingToCart}
    />
  );

  if (!hasMultipleWeights && renderAddToBag) {
    return <>{renderAddToBag({ disabled, label })}</>;
  }

  return (
    <Button
      variant="primary"
      full
      disabled={disabled}
      startIcon={
        isAddingToCart && <CheckIcon className={styles['textContrastColor']} />
      }
      label={label}
      className={styles['addToBagButton']}
      onClick={hasMultipleWeights ? onSelectWeight : onAddToBag}
    />
  );
}
